import styled from '@emotion/styled';
import { FC, ReactNode, useState } from 'react';
import { useNavigate } from 'react-router';

import { useUserSettings } from '../../context/UserSettingsContext';
import { getSideNavigationWorkspaces } from '../util';

import {
  EuiBasicTableColumn,
  FlexGrid,
  FlexGroupWrapper,
  FlexItem,
  Flyout,
  Icon,
  SearchAndActionButton,
  Table,
  Workspace,
} from '@tecton/ComponentRedesign';

// icons
import DevelopmentWorkspaceColor from '@svg/development-workspace-color.svg';
import DevelopmentWorkspace from '@svg/development-workspace.svg';
import LiveWorkspaceColor from '@svg/live-workspace-color.svg';
import LiveWorkspace from '@svg/live-workspace.svg';
import Plus from '@svg/plus.svg';
import Recent from '@svg/recent.svg';
import HowToCreateWorkspace from './HowToCreateWorkspace';

const WorkspaceNameStyle = styled.div`
  width: 100%;
  cursor: pointer;
`;

const RecentTitle = styled.div`
  color: ${({ theme }) => theme.colors.title};
  font-size: ${({ theme }) => theme.font.headingSizes['4']};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  line-height: ${({ theme }) => theme.font.headingLineHeights['3']};
`;

const Title = styled.div`
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.font.headingSizes['4']};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  line-height: ${({ theme }) => theme.font.headingLineHeights['3']};
`;

const TitleWithOutAccess = styled.div`
  color: ${({ theme }) => theme.colors.darkShade};
  font-size: ${({ theme }) => theme.font.headingSizes['4']};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  line-height: ${({ theme }) => theme.font.headingLineHeights['3']};
`;

const WorkspaceLayout: FC<{ header: ReactNode; body: ReactNode }> = ({ header, body }) => {
  return (
    <FlexItem>
      <FlexGrid columns={1} gutterSize="s">
        <FlexItem>{header}</FlexItem>
        <FlexItem>{body}</FlexItem>
      </FlexGrid>
    </FlexItem>
  );
};

const WorkspaceSelector: FC = () => {
  const navigate = useNavigate();
  const { updateWorkspace, allWorkspaces } = useUserSettings();

  // general states
  const [search, setSearch] = useState<string>();
  const [IsFlyoutOpen, setIsFlyoutOpen] = useState<boolean>(false);

  const workspaces = getSideNavigationWorkspaces(allWorkspaces ?? []);

  const onWorkspaceClick = (workspace: any) => {
    updateWorkspace(workspace.name);
    navigate('/repo/' + workspace.name + '/home/dataflow');
  };

  const filterWorkspace = (ws: Workspace) => {
    if (!search) {
      return true;
    }
    return ws.name.toLowerCase().includes(search.toLowerCase());
  };

  const columns: EuiBasicTableColumn<Workspace>[] = [
    {
      name: 'Workspace Name',
      width: '40%',
      render: (workspace: Workspace) => {
        const workspaceRole = allWorkspaces?.find((ws) => {
          return ws.name === workspace.name && workspace.isAccessible;
        });

        const hasWorkspaceAccess = !(workspaceRole === undefined);

        return hasWorkspaceAccess ? (
          <WorkspaceNameStyle
            onClick={() => {
              onWorkspaceClick(workspace);
            }}
          >
            <RecentTitle>
              {workspace?.workspaceType === 'Live' ? (
                <div>
                  <Icon type={LiveWorkspaceColor} /> {workspace.name}
                </div>
              ) : (
                <div>
                  <Icon type={DevelopmentWorkspaceColor} /> {workspace.name}
                </div>
              )}
            </RecentTitle>
          </WorkspaceNameStyle>
        ) : (
          <div>
            <TitleWithOutAccess>
              {workspace?.workspaceType === 'Live' ? (
                <div>
                  <Icon type={LiveWorkspaceColor} /> {workspace.name}
                </div>
              ) : (
                <div>
                  <Icon type={DevelopmentWorkspaceColor} /> {workspace.name}
                </div>
              )}
            </TitleWithOutAccess>
          </div>
        );
      },
    },
  ];

  return (
    <>
      {IsFlyoutOpen && (
        <>
          <Flyout
            title={<>How to create a workspace</>}
            isOpen={IsFlyoutOpen}
            setIsOpen={() => {
              setIsFlyoutOpen(!IsFlyoutOpen);
            }}
            content={<HowToCreateWorkspace />}
          />
        </>
      )}

      <FlexGroupWrapper direction="column" gap="l">
        <FlexItem>
          <SearchAndActionButton
            searchPlaceHolder="Search Workspaces"
            actionButtonLabel={'Create Workspace'}
            actionButtonVariant="emptyAction"
            onActionButtonClick={() => {
              setIsFlyoutOpen(true);
            }}
            onSearchInputChange={(value) => {
              setSearch(value);
            }}
            leftButtonIcon={Plus}
          />
          {workspaces.recent.length > 0 && (
            <>
              <FlexGrid columns={1} gutterSize="s">
                <FlexItem>
                  <RecentTitle>
                    <Icon type={Recent} /> Recently Accessed
                  </RecentTitle>
                </FlexItem>
                <FlexItem>
                  <div style={{ maxHeight: '30vh', overflow: 'auto' }}>
                    <Table items={workspaces.recent.filter(filterWorkspace) ?? []} columns={columns} layout="fixed" />
                  </div>
                </FlexItem>
              </FlexGrid>
            </>
          )}
        </FlexItem>
        {workspaces.live.length > 0 && (
          <WorkspaceLayout
            header={
              <Title>
                <Icon type={LiveWorkspace} /> Live
              </Title>
            }
            body={
              <div style={{ maxHeight: '30vh', overflow: 'auto' }}>
                <Table items={workspaces.live.filter(filterWorkspace) ?? []} columns={columns} layout="fixed" />
              </div>
            }
          />
        )}

        {workspaces.development.length > 0 && (
          <WorkspaceLayout
            header={
              <Title>
                <Icon type={DevelopmentWorkspace} /> Development
              </Title>
            }
            body={
              <div style={{ maxHeight: '30vh', overflow: 'auto' }}>
                <Table items={workspaces.development.filter(filterWorkspace) ?? []} columns={columns} layout="fixed" />
              </div>
            }
          />
        )}
      </FlexGroupWrapper>
    </>
  );
};

export default WorkspaceSelector;
